<template>
    <BaseCard padding="none" class="overflow-hidden">
        <header class="qq-header">
            <div class="details text-ink-muted mb-1">{{ ordinalWord }}</div>
            <div class="body-bold text-ink">{{ resolveName(question.text) }}</div>
        </header>

        <div class="p-5 flex flex-col gap-4">
            <div v-if="question.open_text_synthesis">
                <div class="body-small text-ink-muted mb-2">Response Insights</div>
                <MarkdownText :text="question.open_text_synthesis" class="body-small text-ink leading-relaxed qq-insights" />
            </div>

            <template v-if="question.is_score">
                <div class="grid gap-3 items-end" :style="{ gridTemplateColumns: `repeat(${maxScore}, minmax(0, 1fr))` }">
                    <div v-for="(c, i) in question.distribution" :key="i" class="flex flex-col items-center gap-2">
                        <div class="w-full flex items-end h-[80px]">
                            <div
                                class="w-full qq-bar"
                                :class="c > 0 ? 'qq-bar--filled' : 'qq-bar--empty'"
                                :style="{ height: c > 0 ? 20 + (c / Math.max(...question.distribution, 1)) * 60 + 'px' : '4px' }"></div>
                        </div>
                        <div class="details text-ink-muted">
                            {{ i + 1 }} · <span class="text-ink font-medium">{{ formatCount(c) }}</span>
                        </div>
                    </div>
                </div>
                <HorizontalDivider as="hr" spacing="none" />
                <div class="details text-ink-muted">
                    {{ averagePrefix }}
                    <span class="text-ink font-medium">{{ formatNumber(question.all_avg, "one-decimal") || "—" }}/{{ maxScore }}</span>
                </div>
                <div v-if="showNotObservedCount" class="details text-ink-muted">
                    <i class="bi bi-eye-slash" aria-hidden="true"></i>
                    {{ question.not_observed_count }} marked &ldquo;Not observed&rdquo;
                </div>
            </template>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseCard from "~vue/components/BaseCard.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { ordinalWord as ordinalWordFn, resolveQuestionName } from "~vue/utils/assessmentValidation";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";

import type { PerQuestion } from "./types";

interface Props {
    question: PerQuestion;
    ordinal: number;
    subjectFirstName: string;
    anonymityThreshold: number;
    // False when a team lead is viewing; second-person labels become third person.
    viewerIsSubject?: boolean;
    // LAB-795: a self-assessment-only cycle has no raters, so the score is the
    // participant's own rating rather than an average across people.
    isSelfAssessmentOnly?: boolean;
}

const props = defineProps<Props>();

function resolveName(text: string): string {
    return resolveQuestionName(text, props.subjectFirstName);
}

const maxScore = computed(() => props.question.max_score ?? props.question.distribution.length);

const showNotObservedCount = computed(() => props.question.is_score && props.question.not_observed_count >= props.anonymityThreshold);

const ordinalWord = computed(() => `Question ${ordinalWordFn(props.ordinal)}`);

// LAB-985 weighted-by-subject aggregates render the per-score distribution as
// floats (e.g. 0.3333… for one of three raters). Trim to a single decimal so
// the axis label doesn't spill floating-point noise across the screen — drop
// the trailing zero when the value is already whole.
function formatCount(count: number): string {
    return formatNumber(count, "up-to-one-decimal");
}

const averagePrefix = computed(() => {
    if (props.isSelfAssessmentOnly) {
        return props.viewerIsSubject === false ? `${props.subjectFirstName}'s rating` : "Your rating";
    }
    return "Average";
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .qq-header {
        @apply bg-surface-sunken border-b border-border px-5 py-4;
    }

    .qq-bar {
        @apply rounded-t-md;
    }

    .qq-bar--filled {
        @apply bg-valence-cyan-400;
    }

    .qq-bar--empty {
        @apply bg-border;
    }

    /* Bullet styling for the response-insights markdown output. */
    .qq-insights :deep(ul) {
        @apply list-disc pl-5 flex flex-col gap-2;
    }
}
</style>
